<template>
  <div class="container">
    <vx-card title="Templates" class="mt-2">
      <vs-button
          color="secondary"
          v-for="template in riskFactorTemplates"
          @click="setTemplateHandler(template)"
          class="small mr-2 text-black">
        {{template.name}}
      </vs-button>
      <vs-button
          @click="riskFactorTable.reset()"
        class="small mr-2"
        color="danger">
        Restablecer
      </vs-button>
      <vs-button
          @click="saveRiskFactorHandler"
          class="small">
        Guardar
      </vs-button>
    </vx-card>

    <vs-table :data="[{}]" v-if="isBuilt" class="mt-2">
      <template slot="thead">
        <vs-th>
          Factor
        </vs-th>
        <vs-th></vs-th>
      </template>
      <template v-for="riskFactorCategory in riskFactorTable.riskFactorCategories">
        <vs-tr>
          <vs-td class="bg-dark text-white" :colspan="2">
            {{ riskFactorCategory.name }}
          </vs-td>
        </vs-tr>
        <vs-tr v-for="riskFactor in riskFactorCategory.riskFactors">
          <vs-td>{{ riskFactor.name }}</vs-td>
          <vs-td >
            <vs-checkbox
                v-model="riskFactorTable.getItem(riskFactor.id).selected"></vs-checkbox>
          </vs-td>
        </vs-tr>
      </template>
    </vs-table>
  </div>
</template>

<script>
import { templates } from '@/dbjson/risk-factors/templates.json'
import {mapState} from "vuex";

export default {
  name: 'RiskFactor',
  data: () => ({
    riskFactorTable: null,
    isBuilt: false,
    riskFactorTemplates: []
  }),
  props: {
    projectId: {
      type: Number,
      required: true
    }
  },
  async mounted() {
    this.riskFactorTable = new RiskFactorTable();
    this.riskFactorTable.build();
    this.isBuilt = true;
    this.riskFactorTemplates = templates;

    const { data } = await axios.get(`/api/project/${this.projectId}/risk-factor`)
    if (data.payment_capacity_risk_factors !== null) {
      this.riskFactorTable.setTemplate(data.payment_capacity_risk_factors)
    }
  },
  methods: {
    setTemplateHandler(template) {
      this.riskFactorTable.setTemplate(template.items);
    },
    async saveRiskFactorHandler() {
      this.$vs.loading();

      try {
        await axios.post(`/api/project/${this.projectId}/risk-factor`, {
          'risk_factor_ids': this.riskFactorTable.toRequest()
        })
      } finally {
        this.$vs.loading.close();
      }
    }
  }
}

class RiskFactorTableItem {
  constructor(riskFactor, selected) {
    this.riskFactor = riskFactor;
    this.selected = selected || false;
  }
}

class RiskFactorTable {
  itemIndex = {};

  constructor() {
    this.items = [];
    this.resourceUsages = [
      new ResourceUsage(1, "Activo fijo"),
      new ResourceUsage(2, "Capital de trabajo"),
      new ResourceUsage(3, "Refinanciamiento de deudas"),
      new ResourceUsage(4, "Comercio"),
      new ResourceUsage(5, "Servicio"),
      new ResourceUsage(6, "Producción"),
    ];

    let businessFactors = [
      new RiskFactor(1, "Riesgos de la estrategia de negocio"),
      new RiskFactor(2, "Falta de liquidez para la empresa"),
      new RiskFactor(3, "Incremento de la deuda"),
      new RiskFactor(4, "Dependencia hacia clientes o proveedores clave"),
      new RiskFactor(5, "Mal uso de los recursos"),
    ];

    let commerceFactors = [
      new RiskFactor(6, "Condiciones de mercado adversas"),
      new RiskFactor(7, "Entrada de nuevos competidores a la industria"),
    ];

    let macroEconomicFactors = [
      new RiskFactor(8, "Recesión económica"),
      new RiskFactor(9, "Aumentos en costos de producción o distribución"),
      new RiskFactor(10, "Incremento en precios de materia prima"),
      new RiskFactor(11, "Incremento de determinadas tasas de interés"),
    ];

    let technologyFactors = [
      new RiskFactor(12, "Cambios tecnológicos o de preferencias de los consumidores"),
    ];

    let naturalFactors = [
      new RiskFactor(13, "Desastres naturales"),
      new RiskFactor(14, "Daño de los equipos ocasionado por desastres naturales"),
      new RiskFactor(15, "Fallecimiento del acreditado"),
    ];

    let environmentalFactors = [
      new RiskFactor(16, "Riesgos ambientales relacionados con activos, insumos, productos o servicios"),
    ];

    let legalAndRegulatoryFactors = [
      new RiskFactor(17, "Cambios legales o regulatorios"),
    ];

    let securityFactors = [
      new RiskFactor(18, "Daño o robo de los equipos por vandalismo o delincuencia"),
      new RiskFactor(19, "Daño físico a negocio causado por vandalismo o delincuencia"),
      new RiskFactor(20, "Seguridad de ubicación"),
    ];

    let projectFactors = [
      new RiskFactor(21, "Mala instalación de equipos"),
      new RiskFactor(22, "Dependencia de un tercero para funcionamiento de equipos"),
    ];

    this.riskFactorCategories = [
      new RiskFactorCategory(1, "De la empresa", businessFactors),
      new RiskFactorCategory(2, "Del mercado", commerceFactors),
      new RiskFactorCategory(3, "Macroeconómicos", macroEconomicFactors),
      new RiskFactorCategory(4, "Tecnológicos", technologyFactors),
      new RiskFactorCategory(5, "Naturales", naturalFactors),
      new RiskFactorCategory(6, "Ambientales", environmentalFactors),
      new RiskFactorCategory(7, "Legales, regulatorios", legalAndRegulatoryFactors),
      new RiskFactorCategory(8, "De seguridad", securityFactors),
      new RiskFactorCategory(9, "Del proyecto", projectFactors),
    ];
  }

  build() {
    for (let riskFactorCategory of this.riskFactorCategories) {
      for (let riskFactor of riskFactorCategory.riskFactors) {
        this.add(riskFactor, false);
      }
    }
  }

  /**
   * @param {RiskFactor} riskFactor
   * @param {boolean} selected
   */
  add(riskFactor, selected) {
    if (typeof this.itemIndex[riskFactor.id] === "undefined") {
      this.itemIndex[riskFactor.id] = {}
    }
    let riskFactorItem = new RiskFactorTableItem(riskFactor, selected);
    this.itemIndex[riskFactor.id] = riskFactorItem;
    this.items.push(riskFactorItem)
  }

  /**
   * @param {number} riskFactorId
   * @returns {RiskFactorTableItem}
   */
  getItem(riskFactorId) {
    return this.itemIndex[riskFactorId] || {};
  }

  setTemplate(items) {
    for (let item of items) {
      this.getItem(item).selected = true;
    }
  }

  reset() {
    for (let item of this.items) {
      item.selected = false;
    }
  }

  /**
   * @returns {number[]}
   */
  toRequest() {
    let request = [];
    for(let item of this.items) {
      if (item.selected === true) {
        request.push(item.riskFactor.id)
      }
    }

    return request;
  }
}

class RiskFactorCategory {
  constructor(id, name, riskFactors) {
    this.id = id;
    this.name = name;
    this.riskFactors = riskFactors;
  }

  addRiskFactor(name) {
    this.riskFactors.push(new RiskFactor(name))
  }
}

class RiskFactor {
  constructor(id, name) {
    this.id = id;
    this.name = name;
  }
}

class ResourceUsage {
  constructor(id, name) {
    this.id = id;
    this.name = name;
  }
}
</script>
